import { memo } from 'react';
import PropTypes from 'prop-types';
import MMobileMenu from './MMobileMenu';
import MDesktopMenu from './MDesktopMenu';
import useMAdaptativeMenu from './useMAdaptativeMenu';
import { useBreakpoints } from '../../../hooks/useBreakpoints';

/**
 * A responsive menu component that automatically switches between a mobile and a desktop
 * version based on the screen width. It leverages a custom hook `useMAdaptativeMenu` for shared
 * logic and `useBreakpoints` to determine the current screen size.
 *
 * @param {object} props - The component props.
 * @param {string} [props.align] - Aligns the desktop menu's position relative to the anchor element. Not used in mobile menu.
 * @param {object} props.anchorEl - The DOM element used as the anchor for the desktop menu. Not used in mobile menu.
 * @param {Array} props.options - An array of objects representing the menu options. Each option can include an `id`, `text`, `textVariant`, `caption`, `icon`, `iconVariant`, `filled`, `divider`, and a `handler` function.
 * @param {string} [props.optionName='text'] - Specifies the object property name to use for the menu option text.
 * @param {boolean} props.open - Controls the visibility of the menu.
 * @param {string} props.title - The title displayed at the top of the mobile menu. Not used in desktop menu.
 * @param {function} props.handleClose - A function to execute when the menu is closed.
 * @returns {JSX.Element} Either a `MMobileMenu` or `MDesktopMenu` component, depending on the current screen size.
 */
const MAdaptativeMenu = (props) => {
  const {
    anchorEl,
    anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
    transformOrigin = { vertical: 'top', horizontal: 'center' },
    open,
    optionName = 'text',
    options,
    title,
    handleClose
  } = props;
  const { isTablet } = useBreakpoints();
  const { handleOpen } = useMAdaptativeMenu();

  return isTablet ? (
    <MMobileMenu
      optionName={optionName}
      options={options}
      title={title}
      open={open}
      handleOpen={handleOpen}
      handleClose={handleClose}
    />
  ) : (
    <MDesktopMenu
      optionName={optionName}
      options={options}
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      open={open}
      handleClose={handleClose}
    />
  );
};

MAdaptativeMenu.propTypes = {
  anchorEl: PropTypes.object,
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'center', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right'])
  }),
  transformOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'center', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right'])
  }),
  optionName: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      text: PropTypes.string,
      textVariant: PropTypes.string,
      caption: PropTypes.string,
      icon: PropTypes.string,
      iconVariant: PropTypes.string,
      filled: PropTypes.bool,
      divider: PropTypes.bool,
      handler: PropTypes.func
    })
  ).isRequired,
  open: PropTypes.bool,
  title: PropTypes.string,
  handleClose: PropTypes.func.isRequired
};

export default memo(MAdaptativeMenu);
